import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Radio,
  Row,
  Select,
  Upload,
  AutoComplete,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash, Plus, Inbox, ExternalLink } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../../Components/Navbar/Navbar";
import PageHeader from "../../../Components/PageHeader/PageHeader";
import ViewAttachmentModal from "../../../Components/CustomModals/ViewAttachmentModal";
import ReasonModal from "../../../Components/CustomModals/ReasonModal";
import ConfirmAction from "../../../Components/ConfirmAction/ConfirmAction";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../../Utils/Common";
import { getRequest, postRequest } from "../../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router-dom";

const { Dragger } = Upload;
const { Option } = Select;

interface PettyCashRequisitionFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  review?: boolean;
  approval?: boolean;
}

const PettyCashRequisitionForm: React.FC<PettyCashRequisitionFormProps> = ({
  add,
  edit,
  view,
  review,
  approval,
}) => {
  const [loading,             setLoading]             = useState("");
  const [form]                                        = Form.useForm();
  const [expenseTypes,        setExpenseTypes]        = useState<any[]>([]);
  const [items,               setItems]               = useState<any[]>([]);
  const [branches,            setBranches]            = useState<any>({});
  const [status,              setStatus]              = useState("");
  const [withholdingTaxPercent, setWithholdingTaxPercent] = useState(0);
  const itemsArray = useWatch("itemsArray", form);
  const vatType    = useWatch("vat_type", form);
  const { id: petty_cash_requisition_id } = useParams<{ id: string }>();
  const [attachmentUrls,      setAttachmentUrls]      = useState<string[]>([]);
  const [showAttachment,      setShowAttachment]      = useState<string | null>(null);
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapproveRemarks,   setDisapproveRemarks]   = useState("");

  const isApproving = !!(approval || review);
  const isReadOnly = !!(view || isApproving);

  function clearFields() {
    form.resetFields();
  }

  const subTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.qty) || 0) * (Number(item?.price) || 0);
  }, 0);

  const vatAmount = (() => {
    const gross = subTotal;
    return gross - gross / 1.12;
  })();

  const netVat               = subTotal / 1.12;
  const withholdingTaxAmount = subTotal * (withholdingTaxPercent / 100);
  const grandTotal           = subTotal - withholdingTaxAmount;

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return false;
  };

  const handleUploadChange = (info: any) => {
    const validFiles = info.fileList.filter((f: any) => f.size / 1024 / 1024 < 2);
    form.setFieldValue("attachment", validFiles);
  };

  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      amount: parseFloat(
        ((Number(item.qty) || 0) * (Number(item.price) || 0)).toFixed(2)
      ),
    }));

    const formData = new FormData();

    formData.append("date",           data.date ? dayjs(data.date).format("YYYY-MM-DD") : "");
    formData.append("wht_percentage", String(withholdingTaxPercent));
    formData.append("wht_amount",     String(withholdingTaxAmount));
    formData.append("grand_total",    String(grandTotal));
    formData.append("vat_type",       vatType ?? "");
    formData.append("branch_id",      String(data.branch_id ?? ""));
    formData.append("pc_no",          String(data.pc_no ?? ""));
    formData.append("supplier_name",  String(data.supplier_name ?? ""));
    formData.append("requested_by",   String(data.requested_by ?? ""));
    formData.append("source_from",    String(data.source_from ?? ""));
    formData.append("remarks",        String(data.remarks ?? ""));

    formattedItemsArray.forEach((item: any) => {
      formData.append("item_names[]",       String(item.item_name ?? ""));
      formData.append("expense_type_ids[]", String(item.expense_type_id ?? ""));
      formData.append("qtys[]",             String(item.qty ?? ""));
      formData.append("units[]",            String(item.unit ?? ""));
      formData.append("prices[]",           String(item.price ?? ""));
      formData.append("amounts[]",          String(item.amount ?? ""));
    });

    const attachments = data.attachment || [];
    attachments.forEach((f: any) => {
      if (f.originFileObj) formData.append("attachment[]", f.originFileObj);
    });

    if (edit) {
      formData.append("petty_cash_requisition_id", String(petty_cash_requisition_id));
    }

    setLoading("submit");
    const endpoint = edit ? `petty_cash_requisitions/update` : `petty_cash_requisitions/create`;
    const response = await postRequest(endpoint, formData, true);

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/petty-cash-requisition"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${edit ? "update" : "create"} petty cash.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      `petty_cash_requisitions/change_status`,
      { petty_cash_requisition_id, action: "approved" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/petty-cash-requisition"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve petty cash."
      );
    }
  }

  async function submitCancel() {
    if (!disapproveRemarks.trim()) {
      message.error("Please provide a reason for cancellation.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      `petty_cash_requisitions/change_status`,
      { petty_cash_requisition_id, action: "cancelled", reason: disapproveRemarks },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/petty-cash-requisition"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to cancel petty cash requisition."
      );
    }
  }

  async function fetchBranches() {
    setBranches({});
    setLoading("fetch");
    const response = await getRequest(`branches/get`, {}, true);
    if (response.success) {
      setBranches(response.data.data[0]);
      form.setFieldsValue({ branch_id: response.data.data[0]?.id });
    }
    setLoading("");
  }

  async function fetchExpenseTypes() {
    setExpenseTypes([]);
    setLoading("fetch");
    const response = await getRequest(`expense_types/get`, {}, true);
    if (response.success) setExpenseTypes(response.data.data);
    setLoading("");
  }
  
  async function fetchPettyCashRequisition() {
    setLoading("fetch");
    let nextPcNo = "000001";
    const response = await getRequest(`petty_cash_requisitions/get`, {}, true);
    if (response.success) {
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      const maxPcNo = data.reduce((max: number, row: any) => {
        const num = Number(row?.pc_no);
        return Number.isFinite(num) && num > max ? num : max;
      }, 0);
      nextPcNo = String(maxPcNo + 1).padStart(6, "0");
    }
    form.setFieldsValue({ pc_no: nextPcNo });
    setLoading("");
  }

  async function fetchItems() {
    setLoading("fetch");
    const response = await getRequest(`items/get`, {}, true);
    if (response.success) setItems(response.data.data);
    setLoading("");
  }

  useEffect(() => {
    if ((edit || view || isApproving) && petty_cash_requisition_id) {
      setLoading("fetch");
      getRequest(`petty_cash_requisitions/get`, { petty_cash_requisition_id }, true).then((response) => {
        if (response.success) {
          const data = response.data.data[0];
          setStatus(data.status);
          setWithholdingTaxPercent(Number(data.wht_percentage) || 0);

          // ── Support both array and legacy single URL ──
          if (Array.isArray(data.attachments)) {
            setAttachmentUrls(data.attachments);
          } else if (data.attachment_url) {
            setAttachmentUrls([data.attachment_url]);
          } else {
            setAttachmentUrls([]);
          }

          const items = (data.items || []).map((row: any) => ({
            item_name:       row.item_name,
            expense_type_id: row.expense_type_id,
            qty:             row.qty,
            unit:            row.unit,
            price:           row.price,
          }));

          form.setFieldsValue({
            branch_id:     data.branch_id,
            date:          data.date ? dayjs(data.date) : null,
            pc_no:         data.pc_no,
            supplier_name: data.supplier_name,
            requested_by:  data.requested_by,
            vat_type:      data.vat_type,
            source_from:   data.source_from,
            remarks:       data.remarks,
            itemsArray:    items,
          });
        }
        setLoading("");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchExpenseTypes();
    fetchBranches();
    fetchItems();
    if (add) fetchPettyCashRequisition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = add
    ? "Add Petty Cash Requisition"
    : edit
      ? "Edit Petty Cash Requisition"
      : isApproving
        ? "Approve Petty Cash Requisition"
        : "View Petty Cash Requisition";

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>
          <div className="form-container w-full">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <div
                        style={{
                          padding: "4px 11px",
                          background: "#f5f5f5",
                          border: "1px solid #d9d9d9",
                          borderRadius: "6px",
                          color: "rgba(0,0,0,0.88)",
                          minHeight: "32px",
                        }}
                      >
                        {branches?.name}
                      </div>
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Requisition Date"
                      name="date"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <DatePicker className="w-full" format={"MMMM D, YYYY"} disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item label="Petty Cash Number" name="pc_no">
                      <Input disabled={true} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Supplier Name (Payee)"
                      name="supplier_name"
                      rules={[{ required: !isReadOnly, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Requested By"
                      name="requested_by"
                      rules={[{ required: !isReadOnly, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Vat Type"
                      name="vat_type"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select Vat Type"
                        showSearch
                        allowClear
                        disabled={isReadOnly}
                        filterOption={(input: string, option?: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        <Option value="vat-in">Vat In</Option>
                        <Option value="vat-ex">Vat Ex</Option>
                        <Option value="zero-rated">Zero-Rated</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Row>
                    <span className="edit-label mb-2">
                      {!isReadOnly && <label className="text-red-500 me-1">*</label>}
                      List of Particulars:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full table-fixed border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[100px]">Item Name</th>
                                    <th className="p-2 border w-[80px]">Expense Type</th>
                                    <th className="p-2 border w-[50px]">Qty</th>
                                    <th className="p-2 border w-[60px]">Unit</th>
                                    <th className="p-2 border w-[60px]">Price</th>
                                    <th className="p-2 border w-[60px]">Total</th>
                                    {!isReadOnly && (
                                      <th className="p-2 border w-[40px] text-center">Action</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "item_name"]} rules={[{ required: !isReadOnly, message: "Required" }]} noStyle>
                                          <AutoComplete
                                            disabled={isReadOnly}
                                            placeholder="Item name"
                                            style={{ width: "100%" }}
                                            options={items.map((item: any) => ({
                                              value: item.name,
                                              label: item.name,
                                            }))}
                                            filterOption={(input, option) =>
                                              String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                            }
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "expense_type_id"]} rules={[{ required: !isReadOnly, message: "Required" }]} noStyle>
                                          <Select
                                            className="w-full"
                                            placeholder="Select expense type"
                                            showSearch
                                            allowClear
                                            disabled={isReadOnly}
                                            filterOption={(input: string, option?: any) =>
                                              option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                          >
                                            {expenseTypes.map((type: any) => (
                                              <Select.Option key={type.id} value={type.id}>
                                                {type.name}
                                              </Select.Option>
                                            ))}
                                          </Select>
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "qty"]} rules={[{ required: !isReadOnly, message: "Required" }]} noStyle>
                                          <Input
                                            type="number"
                                            min={1}
                                            disabled={isReadOnly}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "unit"]} rules={[{ required: !isReadOnly, message: "Required" }]} noStyle>
                                          <Input disabled={isReadOnly} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "price"]} rules={[{ required: !isReadOnly, message: "Required" }]} noStyle>
                                          <Input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            disabled={isReadOnly}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const qty   = form.getFieldValue(["itemsArray", field.name, "qty"]);
                                            const price = form.getFieldValue(["itemsArray", field.name, "price"]);
                                            const total = (Number(qty) || 0) * (Number(price) || 0);
                                            return <Input value={formatNumbers(total)} disabled />;
                                          }}
                                        </Form.Item>
                                      </td>
                                      {!isReadOnly && (
                                        <td className="p-2 border text-center">
                                          {fields.length !== 1 && (
                                            <Button onClick={() => remove(field.name)} icon={<Trash size={17} />} danger type="text" />
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                          <Col xs={24}>
                            {!isReadOnly && (
                              <Row>
                                <Col span={17} className="mt-2">
                                  <Button type="primary" onClick={() => add()} icon={<Plus size={16} />}>
                                    Add Item
                                  </Button>
                                </Col>
                              </Row>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Subtotal:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(subTotal)}</Col>
                            </Row>
                            {vatType === "vat-in" && (
                              <>
                                <Row className="mt-2">
                                  <Col span={19} className="text-end"><strong>12% Vat:</strong></Col>
                                  <Col span={5} className="text-start ps-5">₱ {formatNumbers(vatAmount)}</Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col span={19} className="text-end"><strong>Net of Vat:</strong></Col>
                                  <Col span={5} className="text-start ps-5">₱ {formatNumbers(netVat)}</Col>
                                </Row>
                              </>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-1">
                                {isReadOnly ? (
                                  <span className="ps-4">{withholdingTaxPercent}%</span>
                                ) : (
                                  <Radio.Group
                                    value={withholdingTaxPercent}
                                    onChange={(e) => setWithholdingTaxPercent(e.target.value)}
                                  >
                                    <Radio value={0}>0%</Radio>
                                    <Radio value={1}>1%</Radio>
                                    <Radio value={2}>2%</Radio>
                                  </Radio.Group>
                                )}
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Calculated Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(withholdingTaxAmount)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Grand Total:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(grandTotal)}</Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Row>

                  {/* ── Upload (add / edit) ── */}
                  {(edit || add) && (
                    <Col xs={24} className="pt-1">
                      <span className="edit-label mb-2">Deposit Slip(s):</span>
                      <Form.Item
                        name="attachment"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e?.fileList || []}
                        // rules={[{ required: !!add, message: "Deposit slip is required." }]}
                      >
                        <Dragger
                          name="file"
                          multiple
                          beforeUpload={handleBeforeUpload}
                          onChange={handleUploadChange}
                          accept=".pdf,image/*"
                        >
                          <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                          <p className="ant-upload-text">Click or drag files to upload</p>
                          <p className="ant-upload-hint">Supports PDF, Image formats. You can upload multiple files.</p>
                        </Dragger>
                      </Form.Item>
                      <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                    </Col>
                  )}

                  {/* ── View attachments (read-only) ── */}
                  {(view || isApproving) && attachmentUrls.length > 0 && (
                    <Col xs={24} className="pt-1 mt-4">
                      <span className="font-bold block mb-2">Receipt Attachment(s)</span>
                      <div className="flex flex-wrap gap-2">
                        {attachmentUrls.map((url, index) => (
                          <Button
                            key={index}
                            icon={<ExternalLink size={14} />}
                            onClick={() => setShowAttachment(url)}
                          >
                            Attachment {index + 1}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  )}

                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Form.Item>
                      <Button type="primary" onClick={() => { navigateTo("/petty-cash-requisition"); clearFields(); }}>
                        {view || isApproving ? "Close" : "Cancel"}
                      </Button>
                    </Form.Item>

                    {isApproving && (status === "pending" || !status) && (
                      <>
                        <Form.Item>
                          <Button
                            danger
                            loading={loading === "disapprove"}
                            onClick={() => { setDisapproveRemarks(""); setShowDisapproveModal(true); }}
                          >
                            Cancel
                          </Button>
                        </Form.Item>
                        <Form.Item>
                          <ConfirmAction
                            title="Are you sure you want to approve this petty cash requisition?"
                            onConfirm={submitApprove}
                            okText="Approve"
                            cancelText="Cancel"
                            okButtonProps={{ loading: loading === "approve" }}
                          >
                            <Button type="primary" loading={loading === "approve"}>
                              Approve
                            </Button>
                          </ConfirmAction>
                        </Form.Item>
                      </>
                    )}

                    {!view && !isApproving && (
                      <Form.Item>
                        {edit ? (
                          <Button htmlType="submit" type="primary" loading={loading === "submit"}>
                            Update
                          </Button>
                        ) : (
                          <Button htmlType="submit" type="primary" loading={loading === "submit"}>
                            Submit
                          </Button>
                        )}
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />

      <ViewAttachmentModal
        showModal={!!showAttachment}
        attachmentUrl={showAttachment ?? ""}
        onCancel={() => setShowAttachment(null)}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for cancellation:"
        reasonValue={disapproveRemarks}
        onReasonChange={(e: any) => setDisapproveRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Close
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            disabled={!disapproveRemarks.trim()}
            onClick={submitCancel}
          >
            Confirm Cancel
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default PettyCashRequisitionForm;